import { OptionFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { ButtonProps, LinkButtonProps } from "@/components-v2/Button";
import { RowAction } from "@/components-v2/DataTable";
import { DialogProps } from "@/components-v2/Dialog";
import { EmptyStateProps } from "@/components-v2/EmptyState";
import { MTextAreaProps } from "@/components-v2/TextArea";
import { MTextInputProps } from "@/components-v2/TextInput";
import { TabProps } from "@/components-v2/Tabs";
import { TagProps } from "@/components-v2/Tag";
import {
  DCEvent,
  EventDispatcher,
  HttpEventCallbacks,
  showGeneralError,
  UpdateStoreEvent,
} from "@/events/eventsDomain";
import { TMessage } from "@/i18n/tt";
import { TMessageKey } from "@/i18n/types.gen";
import { getRoleTypeLabel, RoleType } from "@/types/enums/RoleType";
import { settingsRoleTypes } from "@/types/Settings";
import {
  AccountRoleActivation,
  accountRoleActivations,
  AccountRoleSystemAccess,
  accountRoleSystemAccesses,
  AccountRoleTypeItem,
  AccountRoleTypeRequest,
  accountRoleTypeApiClient,
} from "./accountRoleTypeApiClient";

export type AccountRoleOrigin = "system" | "account";

export type AccountRoleCompany = { companyId: string; name: string };

export type AccountRole = {
  id: string;
  title: TMessage;
  origin: AccountRoleOrigin;
  activation: AccountRoleActivation;
  selectedCount: number;
  systemAccess: AccountRoleSystemAccess;
};

export const accountRoleFilters = ["all", "system", "account"] as const;

export type AccountRoleFilter = (typeof accountRoleFilters)[number];

export const accountRoleTypeTabs = ["setup", "permissions", "activation"] as const;

export type AccountRoleTypeTab = (typeof accountRoleTypeTabs)[number];

type RoleTypeForm = {
  name: StringFieldModel;
  description: StringFieldModel;
  showDescription: boolean;
  systemAccess: RadioFieldModel<AccountRoleSystemAccess>;
  activation: RadioFieldModel<AccountRoleActivation>;
  companySelection: OptionFieldModel<AccountRoleCompany>;
  showsActivationError: boolean;
  tab: AccountRoleTypeTab;
  isSubmitting: boolean;
};

export type AddRoleTypeDialog = { type: "add" } & RoleTypeForm;

export type EditRoleTypeDialog = { type: "edit"; roleType: AccountRoleTypeItem } & RoleTypeForm;

export type RoleTypeFormDialog = AddRoleTypeDialog | EditRoleTypeDialog;

export type DeleteRoleTypeDialog = {
  type: "delete";
  roleType: AccountRoleTypeItem;
  isSubmitting: boolean;
};

export type AccountRoleTypeDialog = RoleTypeFormDialog | DeleteRoleTypeDialog;

export type AccountCompanyRolesState = {
  accountId: string;
  accountName: string;
  companies: AccountRoleCompany[];
  filter: AccountRoleFilter;
  search: StringFieldModel;
  roleTypes: AccountRoleTypeItem[];
  dialog?: AccountRoleTypeDialog;
};

const rolesWithoutSystemAccess: RoleType[] = [RoleType.CEO, RoleType.OBSERVER];

export const systemAccountRoles: AccountRole[] = settingsRoleTypes.map((roleType) => ({
  id: roleType,
  title: getRoleTypeLabel(roleType),
  origin: "system",
  activation: "ALL_COMPANIES",
  selectedCount: 0,
  systemAccess: rolesWithoutSystemAccess.includes(roleType) ? "NONE" : "BOARD_PORTAL",
}));

const defaultAccountCompanyRolesState = (
  accountId: string,
  accountName: string,
  companies: AccountRoleCompany[],
): AccountCompanyRolesState => ({
  accountId,
  accountName,
  companies,
  filter: "all",
  search: new StringFieldModel(),
  roleTypes: [],
});

export const initAccountCompanyRoles = (
  accountId: string,
  accountName: string,
  companies: AccountRoleCompany[],
): DCEvent[] => [
  {
    type: "update-store",
    data: {
      accountCompanyRoles: (state) =>
        state?.accountId === accountId
          ? { ...state, accountName, companies, dialog: undefined }
          : defaultAccountCompanyRolesState(accountId, accountName, companies),
    },
  },
  accountRoleTypeApiClient.list(accountId, {
    onSuccess: (res) => [updateAccountCompanyRoles(accountId, { roleTypes: res.data })],
    onFailure: () => [showGeneralError()],
  }),
];

const updateAccountCompanyRoles = (
  accountId: string,
  partial: Partial<AccountCompanyRolesState>,
): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    accountCompanyRoles: (state) =>
      state?.accountId === accountId ? { ...state, ...partial } : state,
  },
});

const toAccountRole = (roleType: AccountRoleTypeItem): AccountRole => ({
  id: roleType.id,
  title: { translated: roleType.name },
  origin: "account",
  activation: roleType.activation,
  selectedCount: roleType.selectedCompanyIds.length,
  systemAccess: roleType.systemAccess,
});

export const accountRolesFor = (
  state: AccountCompanyRolesState,
  filter: AccountRoleFilter,
): AccountRole[] =>
  [...systemAccountRoles, ...state.roleTypes.map(toAccountRole)].filter(
    (role) => filter === "all" || role.origin === filter,
  );

const accountRoleFilterLabel = (
  state: AccountCompanyRolesState,
  filter: AccountRoleFilter,
): TMessage => {
  switch (filter) {
    case "all":
      return { key: "enterprise-account.company-roles.filter.all" };
    case "system":
      return {
        key: "enterprise-account.company-roles.filter.system",
        params: { count: accountRolesFor(state, filter).length },
      };
    case "account":
      return {
        key: "enterprise-account.company-roles.filter.account",
        params: { accountName: state.accountName, count: accountRolesFor(state, filter).length },
      };
  }
};

export const prepAccountRoleFilterTabs = (
  state: AccountCompanyRolesState,
  dispatch: EventDispatcher,
): TabProps[] =>
  accountRoleFilters.map((filter) => ({
    id: filter,
    label: accountRoleFilterLabel(state, filter),
    isActive: state.filter === filter,
    onSelect: () => dispatch([updateAccountCompanyRoles(state.accountId, { filter })]),
  }));

export const accountRolesDescription = (state: AccountCompanyRolesState): TMessage => ({
  key: "enterprise-account.company-roles.description",
  params: { accountName: state.accountName },
});

export const prepAddAccountRoleButton = (state: AccountCompanyRolesState): ButtonProps => ({
  variant: "secondary",
  icon: "plus",
  label: { key: "enterprise-account.company-roles.add" },
  onClick: openAddRoleTypeDialog(state.accountId),
});

export const prepAccountRolesSearch = (state: AccountCompanyRolesState): MTextInputProps => ({
  model: state.search,
  placeholder: { key: "search" },
  icon: "magnifying-glass",
  type: "search",
  inline: true,
});

export const prepAccountRolesEmptyState = (state: AccountCompanyRolesState): EmptyStateProps => ({
  illustration: "user-circle-gear",
  subtle: true,
  title: {
    key: "enterprise-account.company-roles.empty",
    params: { accountName: state.accountName },
  },
});

export const accountRoleSubtitle = (
  state: AccountCompanyRolesState,
  role: AccountRole,
): TMessage => {
  switch (role.origin) {
    case "system":
      return { key: "enterprise-account.company-roles.system-role" };
    case "account":
      return {
        key: "enterprise-account.company-roles.account-role",
        params: { accountName: state.accountName },
      };
  }
};

export const prepAccountRoleActivationTag = (role: AccountRole): TagProps => {
  switch (role.activation) {
    case "ALL_COMPANIES":
      return { label: { key: "all-companies" }, color: "success" };
    case "SELECTED_COMPANIES":
      return { label: String(role.selectedCount), color: "info" };
    case "HIDDEN":
      return { label: { key: "hidden" }, color: "neutral" };
  }
};

const activationLabel = (
  activation: AccountRoleActivation,
  selected: number,
  total: number,
): TMessage => {
  switch (activation) {
    case "ALL_COMPANIES":
      return { key: "all-companies" };
    case "SELECTED_COMPANIES":
      return {
        key: "enterprise-account.company-roles.activation-selected",
        params: { selected, total },
      };
    case "HIDDEN":
      return { key: "hidden" };
  }
};

const systemAccessLabels: { [key in AccountRoleSystemAccess]: TMessageKey } = {
  NONE: "enterprise-account.company-roles.system-access.none",
  BOARD_PORTAL: "enterprise-account.company-roles.system-access.board-portal",
};

export const accountRoleSystemAccessLabel = (role: AccountRole): TMessage => ({
  key: systemAccessLabels[role.systemAccess],
});

export const prepAccountRoleActions = (
  state: AccountCompanyRolesState,
  role: AccountRole,
): RowAction[] => {
  const roleType = state.roleTypes.find((candidate) => candidate.id === role.id);
  return roleType
    ? [
        [
          "context-menu",
          [
            {
              type: "item",
              icon: "edit",
              label: { key: "edit" },
              onSelect: openEditRoleTypeDialog(state.accountId, roleType),
            },
            {
              type: "item",
              icon: "trash",
              label: { key: "settings.delete" },
              color: "error",
              onSelect: openDeleteRoleTypeDialog(state.accountId, roleType),
            },
          ],
        ],
      ]
    : [];
};

const isSameName = (a: string, b: string) =>
  a.trim().toLocaleLowerCase() === b.trim().toLocaleLowerCase();

const roleTypeNameField = (others: AccountRoleTypeItem[], value?: string) =>
  new StringFieldModel({
    value,
    required: true,
    validator: (name) =>
      others.some((other) => isSameName(other.name, name))
        ? { key: "enterprise-account.company-roles.name-taken" }
        : undefined,
  });

const systemAccessField = (value: AccountRoleSystemAccess) =>
  new RadioFieldModel<AccountRoleSystemAccess>({
    value,
    options: [...accountRoleSystemAccesses],
    idFn: (access) => access,
    labelFn: (access) => ({ key: systemAccessLabels[access] }),
  });

const activationFields = (
  companies: AccountRoleCompany[],
  activation: AccountRoleActivation,
  selectedCompanyIds: string[],
): Pick<RoleTypeForm, "activation" | "companySelection" | "showsActivationError"> => {
  const companySelection = new OptionFieldModel<AccountRoleCompany>({
    options: companies,
    idFn: (company) => company.companyId,
    labelFn: (company) => ({ translated: company.name }),
    selectedIds: selectedCompanyIds,
  });
  return {
    companySelection,
    activation: new RadioFieldModel<AccountRoleActivation>({
      value: activation,
      options: [...accountRoleActivations],
      idFn: (option) => option,
      labelFn: (option) =>
        activationLabel(option, companySelection.selectedIds.size, companies.length),
    }),
    showsActivationError: false,
  };
};

const openDialog = (
  accountId: string,
  dialog: (state: AccountCompanyRolesState) => AccountRoleTypeDialog,
): DCEvent[] => [
  {
    type: "update-store",
    data: {
      accountCompanyRoles: (state) =>
        state?.accountId === accountId ? { ...state, dialog: dialog(state) } : state,
    },
  },
];

export const openAddRoleTypeDialog = (accountId: string): DCEvent[] =>
  openDialog(accountId, (state) => ({
    type: "add",
    name: roleTypeNameField(state.roleTypes),
    description: new StringFieldModel(),
    showDescription: false,
    systemAccess: systemAccessField("NONE"),
    ...activationFields(state.companies, "ALL_COMPANIES", []),
    tab: "setup",
    isSubmitting: false,
  }));

const openEditRoleTypeDialog = (accountId: string, roleType: AccountRoleTypeItem): DCEvent[] =>
  openDialog(accountId, (state) => ({
    type: "edit",
    roleType,
    name: roleTypeNameField(
      state.roleTypes.filter((other) => other.id !== roleType.id),
      roleType.name,
    ),
    description: new StringFieldModel({ value: roleType.description }),
    showDescription: Boolean(roleType.description),
    systemAccess: systemAccessField(roleType.systemAccess),
    ...activationFields(state.companies, roleType.activation, roleType.selectedCompanyIds),
    tab: "setup",
    isSubmitting: false,
  }));

const openDeleteRoleTypeDialog = (accountId: string, roleType: AccountRoleTypeItem): DCEvent[] =>
  openDialog(accountId, () => ({ type: "delete", roleType, isSubmitting: false }));

const setDialogSubmitting = (accountId: string, isSubmitting: boolean): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    accountCompanyRoles: (state) =>
      state?.accountId === accountId && state.dialog
        ? { ...state, dialog: { ...state.dialog, isSubmitting } }
        : state,
  },
});

const updateFormDialog = (accountId: string, partial: Partial<RoleTypeForm>): UpdateStoreEvent => ({
  type: "update-store",
  data: {
    accountCompanyRoles: (state) =>
      state?.accountId === accountId && state.dialog && state.dialog.type !== "delete"
        ? { ...state, dialog: { ...state.dialog, ...partial } }
        : state,
  },
});

const roleTypesSaved = (accountId: string): HttpEventCallbacks<AccountRoleTypeItem[]> => ({
  onSuccess: (res) => [
    updateAccountCompanyRoles(accountId, { roleTypes: res.data, dialog: undefined }),
  ],
  onFailure: () => [setDialogSubmitting(accountId, false), showGeneralError()],
});

const formDescription = (dialog: RoleTypeFormDialog): string =>
  dialog.showDescription ? dialog.description.inputValue.trim() : "";

const formSelectedCompanyIds = (dialog: RoleTypeFormDialog): string[] =>
  dialog.activation.value === "SELECTED_COMPANIES"
    ? dialog.companySelection.selected.map((company) => company.companyId)
    : [];

const isSameSet = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value) => b.includes(value));

const isChanged = (dialog: EditRoleTypeDialog): boolean =>
  dialog.name.isChanged ||
  formDescription(dialog) !== (dialog.roleType.description ?? "") ||
  dialog.systemAccess.value !== dialog.roleType.systemAccess ||
  dialog.activation.value !== dialog.roleType.activation ||
  !isSameSet(formSelectedCompanyIds(dialog), dialog.roleType.selectedCompanyIds);

const lacksCompanies = (dialog: RoleTypeFormDialog): boolean =>
  dialog.activation.value === "SELECTED_COMPANIES" && dialog.companySelection.isNoneSelected;

export const accountRoleTypeActivationError = (dialog: RoleTypeFormDialog): TMessage | undefined =>
  dialog.showsActivationError && lacksCompanies(dialog)
    ? { key: "enterprise-account.company-roles.companies-required" }
    : undefined;

const submitRoleType =
  (
    accountId: string,
    dialog: RoleTypeFormDialog,
    submit: (request: AccountRoleTypeRequest) => DCEvent[],
  ) =>
  (): DCEvent[] => {
    dialog.name.commit();
    if (!dialog.name.isValid) {
      return [updateFormDialog(accountId, { tab: "setup" })];
    }
    if (lacksCompanies(dialog)) {
      return [updateFormDialog(accountId, { tab: "activation", showsActivationError: true })];
    }
    return submit({
      name: dialog.name.validatedValue,
      description: formDescription(dialog) || undefined,
      systemAccess: dialog.systemAccess.value,
      activation: dialog.activation.value,
      selectedCompanyIds: formSelectedCompanyIds(dialog),
    });
  };

const addRoleType = (accountId: string, request: AccountRoleTypeRequest): DCEvent[] => [
  setDialogSubmitting(accountId, true),
  accountRoleTypeApiClient.add(accountId, request, roleTypesSaved(accountId)),
];

const updateRoleType = (
  accountId: string,
  roleType: AccountRoleTypeItem,
  request: AccountRoleTypeRequest,
): DCEvent[] => [
  setDialogSubmitting(accountId, true),
  accountRoleTypeApiClient.update(accountId, roleType.id, request, roleTypesSaved(accountId)),
];

const deleteRoleType = (accountId: string, roleType: AccountRoleTypeItem): DCEvent[] => [
  setDialogSubmitting(accountId, true),
  accountRoleTypeApiClient.delete(accountId, roleType.id, roleTypesSaved(accountId)),
];

export const prepAccountRoleTypeDialog = (
  state: AccountCompanyRolesState,
  dialog: AccountRoleTypeDialog,
): DialogProps => {
  const common: DialogProps = {
    maxWidth: "xs",
    isSubmitting: dialog.isSubmitting,
    onCancel: [updateAccountCompanyRoles(state.accountId, { dialog: undefined })],
  };
  switch (dialog.type) {
    case "add":
      return {
        ...common,
        maxWidth: "sm",
        title: { key: "enterprise-account.company-roles.add-title" },
        submitLabel: { key: "save" },
        isSubmitDisabled: dialog.name.isEmpty,
        onSubmit: submitRoleType(state.accountId, dialog, (request) =>
          addRoleType(state.accountId, request),
        ),
      };
    case "edit":
      return {
        ...common,
        maxWidth: "sm",
        title: { key: "enterprise-account.company-roles.edit-title" },
        submitLabel: { key: "save" },
        isSubmitDisabled: dialog.name.isEmpty || !isChanged(dialog),
        onSubmit: submitRoleType(state.accountId, dialog, (request) =>
          updateRoleType(state.accountId, dialog.roleType, request),
        ),
      };
    case "delete":
      return {
        ...common,
        title: { key: "enterprise-account.company-roles.delete-title" },
        submitLabel: { key: "settings.delete" },
        submitColor: "error",
        isSubmitDisabled: dialog.roleType.holderCount > 0,
        onSubmit: deleteRoleType(state.accountId, dialog.roleType),
      };
  }
};

const accountRoleTypeTabLabels: { [key in AccountRoleTypeTab]: TMessageKey } = {
  setup: "enterprise-account.company-roles.tab-setup",
  permissions: "enterprise-account.company-roles.tab-permissions",
  activation: "enterprise-account.company-roles.tab-activation",
};

export const prepAccountRoleTypeTabs = (
  state: AccountCompanyRolesState,
  dialog: RoleTypeFormDialog,
): TabProps[] =>
  accountRoleTypeTabs.map((tab) => ({
    id: tab,
    label: { key: accountRoleTypeTabLabels[tab] },
    isActive: dialog.tab === tab,
    onSelect: [updateFormDialog(state.accountId, { tab })],
  }));

export const prepAccountRoleTypeNameInput = (dialog: RoleTypeFormDialog): MTextInputProps => ({
  model: dialog.name,
  label: { key: "enterprise-account.company-roles.title" },
  autofocus: true,
});

export const prepAddDescriptionButton = (state: AccountCompanyRolesState): LinkButtonProps => ({
  icon: "plus",
  label: { key: "enterprise-account.company-roles.add-description" },
  onClick: [updateFormDialog(state.accountId, { showDescription: true })],
});

export const prepRemoveDescriptionButton = (state: AccountCompanyRolesState): LinkButtonProps => ({
  label: { key: "remove" },
  onClick: () => [
    updateFormDialog(state.accountId, {
      showDescription: false,
      description: new StringFieldModel(),
    }),
  ],
});

export const prepAccountRoleTypeDescriptionInput = (
  dialog: RoleTypeFormDialog,
): MTextAreaProps => ({
  model: dialog.description,
  label: { key: "enterprise-account.company-roles.role-description" },
  rows: 3,
  autofocus: dialog.description.isEmpty,
});

export const accountRoleTypeActivationIntro = (state: AccountCompanyRolesState): TMessage => ({
  key: "enterprise-account.company-roles.activation-intro",
  params: { accountName: state.accountName },
});

export const accountRoleTypeDeleteMessage = ({ roleType }: DeleteRoleTypeDialog): TMessage =>
  roleType.holderCount > 0
    ? {
        key: "enterprise-account.company-roles.delete-in-use",
        params: { count: roleType.holderCount, name: roleType.name },
      }
    : {
        key: "enterprise-account.company-roles.delete-description",
        params: { name: roleType.name },
      };
